import React from "react";
import EbDialog from "../ExecutionBlueprintLiveReview/_shared/EbDialog";
import "../ExecutionBlueprintReview/InfoDialog.scss";

// ═════════════════════════════════════════════════════════════════════════════
// "How WIG Connections works" — the explainer for live review stage 2.
//
// ⚠️ ONCE PER USER — see `WIG_CONNECTIONS_INFO_SEEN_KEY` on the screen. It was
// briefly every-visit; the instruction was revised the same day to "only shown
// one time but everytime for new user", which is the feature's usual one-time
// convention and what `ebLiveReviewInfoSeen` already does.
//
// The flag is NOT keyed by blueprint id, unlike `scReviewInfoSeen`. What this
// dialog teaches is a gesture — drag from a point to connect, drag a card to
// reorder — and a gesture is learnt once, not once per client. Someone who has
// run a live review before does not need teaching again on their next one.
//
// Why it matters that it appears at all: this is the only screen in the product
// whose primary interactions are DRAGS, and a drag has no affordance that
// survives not being told about it. The canvas's hint line does say so, but the
// connector half only after a card is selected — which is itself the step
// someone who doesn't know the gesture never takes.
//
// The toolbar's "How connections work" button reopens it any time, which is
// what makes once-per-user safe rather than a one-shot the user can miss.
//
// ⚠️ THIS IS THE COACH'S VARIANT AND IT DIFFERS FROM THE CEO'S. Same rule as
// `LiveReviewInfoModal`: do NOT re-sync the two by diff. Both people are on this
// screen at once and both can draw connections, but only the CEO is being asked
// about their own business — the coach is being asked to draw it out of them.
//
// ⚠️ THE STEPS ARE JSX FRAGMENTS, NOT STRINGS — they carry bolded phrases, so
// they cannot be keyed by their own text the way every other list in this
// feature is. Keyed by index instead, which is safe here for the one reason it
// is ever safe: the list is a frozen constant that never reorders.
// ═════════════════════════════════════════════════════════════════════════════

// Each step is one gesture, in the order they get used: look, then add, then
// correct, then finish. The wording matches the canvas's own hint line verbatim
// where it can ("drag from a point", "press Delete") so the dialog and the
// screen teach the same words for the same action.
const STEPS = [
  <>
    <strong>Click any WIG</strong> to see what it already connects to —
    everything it contributes to, and everything it depends on
  </>,
  <>
    <strong>Drag from a connection point</strong> on that card to another WIG to
    add a link
  </>,
  <>
    Click a line to select it, then drag either end to reconnect it or press{" "}
    <strong>Delete</strong> to remove it
  </>,
  <>
    <strong>Drag a card sideways</strong> to reorder its row — WIGs stay in
    their own dimension, so this changes the layout, not the plan
  </>,
  <>
    <strong>Confirm Connections</strong> once you and the CEO agree the map is
    right
  </>,
];

const WigConnectionsInfoModal = ({ onClose }) => (
  <EbDialog
    isOpen
    size="lg"
    // Neutral: this explains rather than asks, and a teal chip would give it the
    // weight of a decision.
    tone="neutral"
    title="How WIG Connections works"
    subtitle="This map shows how the CEO's WIGs contribute to each other and, together, to their BHAG."
    onClose={onClose}
    actions={[
      { label: "Okay", variant: "primary", onClick: onClose, autoFocus: true },
    ]}
  >
    {/* Names the draft as a draft. The screen opens on a map that is anything
        but blank, and a coach who takes it as given has nothing to run this
        stage on — the conversation IS the disagreements with it. */}
    <p>
      The connections on screen are drafted from the blueprint — a starting
      point, not the answer. The work of this stage is going through them
      together: what&apos;s missing, what doesn&apos;t hold, and what the CEO
      would draw differently. Both of you can edit the map.
    </p>

    <ol className="info-steps">
      {STEPS.map((step, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={index}>{step}</li>
      ))}
    </ol>
  </EbDialog>
);

export default WigConnectionsInfoModal;
